'use client';

/**
 * Public schoolhouse browse page.
 *
 * Module: app/schoolhouse/page
 * Layer:  page
 *
 * Purpose:
 *     Renders the "Find Your Schoolhouse" hero, a search + featured + sort
 *     toolbar, and a responsive grid of {@link SchoolhouseCard}s with loading
 *     and empty states. Data + filter state live in {@link useSchoolhouses};
 *     this file is presentational so every unit clears the CC<=5 / <=60-line
 *     gates.
 *
 * AI context:
 *     - Entry points called by: Next.js app router (route `/schoolhouse`).
 *     - Depends on: components/Nav, components/schoolhouse/SchoolhouseCard,
 *       ./use-schoolhouses, lib/i18n.
 *     - Side effects: none directly (network I/O is inside the hook).
 *     - Invariants: every visible string flows through `t()`.
 *     - Replacement cost: LOW.
 */

import { type ChangeEvent, type SubmitEvent, useCallback } from 'react';
import Nav from '@/components/Nav';
import { SchoolhouseCard } from '@/components/schoolhouse/SchoolhouseCard';
import { type TranslationKey, useTranslation } from '@/lib/i18n';
import type { SchoolhouseSortBy } from '@/lib/schoolhouse';
import { type SchoolhousesController, useSchoolhouses } from './use-schoolhouses';

/** Sort options rendered in display order (data-driven). */
const SORT_OPTIONS: readonly { readonly value: SchoolhouseSortBy; readonly labelKey: TranslationKey }[] = [
  { value: 'featured', labelKey: 'schoolhouse.sort.featured' },
  { value: 'rating', labelKey: 'schoolhouse.sort.rating' },
  { value: 'students', labelKey: 'schoolhouse.sort.students' },
  { value: 'recent', labelKey: 'schoolhouse.sort.recent' },
] as const;

/** Hero banner with the page title + subtitle. */
function Hero(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='bg-gradient-to-r from-purple-700 to-purple-900 text-white'>
      <div className='mx-auto max-w-7xl px-4 py-12'>
        <h1 className='text-3xl font-bold md:text-4xl'>{t('schoolhouse.hero.title')}</h1>
        <p className='mt-2 text-lg text-purple-100'>{t('schoolhouse.hero.subtitle')}</p>
      </div>
    </div>
  );
}

/** Centered loading spinner. */
function LoadingState(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='py-12 text-center'>
      <div className='mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-purple-600 border-t-transparent' />
      <p className='opacity-70'>{t('schoolhouse.loading')}</p>
    </div>
  );
}

/** Sort selector driven by the {@link SORT_OPTIONS} table. */
function SortSelect(props: { readonly ctrl: SchoolhousesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>): void => {
      ctrl.setSortBy(e.target.value as SchoolhouseSortBy);
    },
    [ctrl],
  );
  return (
    <select
      value={ctrl.sortBy}
      onChange={onChange}
      aria-label={t('schoolhouse.sortLabel')}
      className='appearance-none rounded-xl border bg-white px-4 py-3 focus:ring-2 focus:ring-purple-500'
    >
      {SORT_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {t(opt.labelKey)}
        </option>
      ))}
    </select>
  );
}

/** Search field, featured toggle, and sort selector. */
function Toolbar(props: { readonly ctrl: SchoolhousesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const onSubmit = useCallback(
    (e: SubmitEvent<HTMLFormElement>): void => {
      e.preventDefault();
      ctrl.submitSearch();
    },
    [ctrl],
  );
  const onQueryChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      ctrl.setDraftQuery(e.target.value);
    },
    [ctrl],
  );
  return (
    <div className='mb-8 flex flex-col gap-4 md:flex-row'>
      <form onSubmit={onSubmit} className='flex flex-1 gap-2'>
        <input
          type='text'
          value={ctrl.draftQuery}
          onChange={onQueryChange}
          placeholder={t('schoolhouse.searchPlaceholder')}
          className='w-full rounded-xl border px-4 py-3 focus:border-purple-500 focus:ring-2 focus:ring-purple-500'
        />
        <button
          type='submit'
          className='rounded-xl bg-purple-600 px-5 py-3 font-medium text-white hover:bg-purple-700'
        >
          {t('schoolhouse.search')}
        </button>
      </form>
      <div className='flex gap-4'>
        <FeaturedToggle ctrl={ctrl} />
        <SortSelect ctrl={ctrl} />
      </div>
    </div>
  );
}

/** Featured-only toggle button. */
function FeaturedToggle(props: { readonly ctrl: SchoolhousesController }): React.JSX.Element {
  const { t } = useTranslation();
  const { ctrl } = props;
  const active = ctrl.featuredOnly ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200';
  return (
    <button
      type='button'
      onClick={ctrl.toggleFeatured}
      aria-pressed={ctrl.featuredOnly}
      className={`rounded-xl px-4 py-3 text-sm font-medium transition-colors ${active}`}
    >
      {t('schoolhouse.featuredOnly')}
    </button>
  );
}

/** Empty-state panel with a clear-filters action. */
function EmptyResults(props: { readonly onClear: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className='rounded-xl bg-gray-100 py-12 text-center'>
      <h3 className='mb-2 text-lg font-semibold'>{t('schoolhouse.none.title')}</h3>
      <p className='mb-4 opacity-70'>{t('schoolhouse.none.subtitle')}</p>
      <button
        type='button'
        onClick={props.onClear}
        className='rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700'
      >
        {t('schoolhouse.none.clear')}
      </button>
    </div>
  );
}

/** Responsive grid of result cards, or the empty state. */
function Results(props: { readonly ctrl: SchoolhousesController }): React.JSX.Element {
  const { ctrl } = props;
  if (ctrl.schools.length === 0) {
    return <EmptyResults onClear={ctrl.clearFilters} />;
  }
  return (
    <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
      {ctrl.schools.map((school) => (
        <SchoolhouseCard key={school.id} school={school} />
      ))}
    </div>
  );
}

export default function SchoolhousePage(): React.JSX.Element {
  const ctrl = useSchoolhouses();
  return (
    <div className='min-h-screen bg-gray-50'>
      <Nav />
      <Hero />
      <main className='mx-auto max-w-7xl px-4 py-8'>
        <Toolbar ctrl={ctrl} />
        {ctrl.loading ? <LoadingState /> : <Results ctrl={ctrl} />}
      </main>
    </div>
  );
}
